import { types as sdkTypes } from './sdkLoader';

const { Money } = sdkTypes;

export const getBrandDisplayName = (brandValue, listingConfig) => {
  if (!brandValue) return null;
  const brandStr = Array.isArray(brandValue) ? brandValue[0] : brandValue;
  if (typeof brandStr !== 'string') return brandStr;
  const brandFieldConfig = listingConfig?.listingFields?.find(field => field.key === 'brand');
  const brandEnumOptions = brandFieldConfig?.enumOptions || [];
  const brandOption = brandEnumOptions.find(option => option.option === brandStr);
  if (brandOption) return brandOption.label;
  return brandStr
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const getListingMoney = listing => {
  const price = listing?.attributes?.price;
  if (!price) return null;
  const amount =
    typeof price.amount === 'number'
      ? price.amount
      : typeof price.minor_unit === 'number'
      ? price.minor_unit
      : null;
  const currency = price.currency;
  if (amount == null || !currency) return null;
  try {
    return new Money(amount, currency);
  } catch (e) {
    return null;
  }
};

export const computeBundlePricing = (bundle, productListingsById) => {
  let rawSubtotal = 0;
  let currency = null;
  const productsWithListings = [];
  const missingProducts = [];

  for (const product of bundle.products || []) {
    const listing = productListingsById[product.id];
    if (!listing) {
      missingProducts.push(product);
      productsWithListings.push({ product, listing: null, money: null });
      continue;
    }
    const money = getListingMoney(listing);
    productsWithListings.push({ product, listing, money });
    if (money) {
      rawSubtotal += money.amount * Math.max(1, Number(product.quantity) || 1);
      currency = currency || money.currency;
    }
  }

  const price = bundle.price || {};
  const discountPct =
    price.discount != null && Number.isFinite(Number(price.discount))
      ? Math.max(0, Math.min(100, Number(price.discount)))
      : null;

  let finalAmount = rawSubtotal;
  let mode = 'raw';
  let showStrikethrough = false;

  if (price.amount != null && Number.isFinite(Number(price.amount))) {
    const fixed = Math.max(0, Math.round(Number(price.amount)));
    finalAmount = fixed;
    mode = 'fixed';
    showStrikethrough = fixed < rawSubtotal;
  } else if (discountPct != null && discountPct > 0) {
    finalAmount = Math.round(rawSubtotal * (1 - discountPct / 100));
    mode = 'percentage';
    showStrikethrough = true;
  }

  const rawRounded = Math.round(rawSubtotal);
  const finalRounded = Math.round(finalAmount);

  let badgeDiscountSubunits = null;
  if (showStrikethrough && rawRounded > finalRounded && currency) {
    badgeDiscountSubunits = rawRounded - finalRounded;
  }

  return {
    rawSubtotal: rawRounded,
    finalAmount: finalRounded,
    currency,
    discountPct,
    mode,
    showStrikethrough,
    badgeDiscountSubunits,
    productsWithListings,
    hasMissingProducts: missingProducts.length > 0,
  };
};

/** Returns true if the bundle has any configured price reduction. */
export const bundleHasDiscount = bundle => {
  const price = bundle?.price || {};
  const discountPct =
    price.discount != null && Number.isFinite(Number(price.discount))
      ? Number(price.discount)
      : 0;
  return discountPct > 0 || (price.amount != null && Number.isFinite(Number(price.amount)));
};
